<template>
  <div>
    <page-header
      :actions="headerActionsConfig"
      left-side-class="col-12 col-md-7"
      :page="headerConfig"
      right-side-class="col-12 col-md-5"
    />
    <create-contract
      v-if="isVisibleCreateContract"
      :academic-years="filterOptions.academicYears"
      class="mb-5"
      :contract-types="filterOptions.contractTypes"
      @on-cancel="handleCreateContractCancel"
      @on-continue="handleCreateContractContinue"
    />
    <filter-manager
      v-model="filterModel"
      class="mb-4"
      :display-advanced="false"
      :filters="filtersConfig"
      @search="handleSearch"
    />
    <custom-separator class="my-4" text="Resultado de Contratos" />
    <custom-result-not-found v-if="results.length == 0" />
    <template v-else>
      <result-row
        v-for="(row, index) of results"
        :key="`result-${index}`"
        class="mb-3"
        :data="row"
        @on-create-new-version="handleCreateNewVersion"
        @on-discard-draft="handleDiscardDraft"
        @on-edit="handleEdit"
        @on-print="onPrintHandler($event)"
        @on-view-version="handleViewVersion(row, $event)"
        @toggle="handleToggle"
      />
      <pagination
        v-if="pagination.totalItemCount > pagination.pageSize"
        :data="pagination"
        show-text
        @pagination-go-page="goToPageHandler"
      />
    </template>
  </div>
</template>

<script>
// libs
import PageHeader from 'PageHeader';
import FilterManager from 'FilterManager';
import CustomSeparator from 'custom-separator';
import CustomResultNotFound from 'custom-result-not-found';

// services
import {
  getInitFilters as _getInitFilters,
  search as _search,
  checkForExistingContractEditor as _checkForExistingContractEditor,
  discardDraftVersion as _discardDraftVersion,
  createNewVersion as _createNewVersion,
  saveContractEditor as _saveContractEditor,
} from '@/services/provider/contract-editor.api';

// mixins
import storeMixin from '@quisit/mipe-utils/mixins/storeGenerator';
import alertMixin from '@quisit/mipe-utils/mixins/alert.mixin.js';

// child components
import { ResultRow, CreateContract } from './components';

// constants
import storeModuleNames from '@/utils/constants/storeModuleNames.js';
const COMPONENT_NAME = 'ContractEditorMain';
const DEFAULT_PAGE_SIZE = 10;
const CONTRACT_EDITOR_MANAGEMENT_ROUTE_NAME = 'contractEditorManagement';

export default {
  name: COMPONENT_NAME,
  mixins: [storeMixin, alertMixin],
  components: {
    PageHeader,
    FilterManager,
    CustomSeparator,
    CustomResultNotFound,
    ResultRow,
    CreateContract,
  },
  data() {
    return {
      moduleName: storeModuleNames.UspqrpContractEditorModuleName,
      filterModel: {
        academicYear: undefined,
        contractType: undefined,
      },
      filterOptions: {
        academicYears: [],
        contractTypes: [],
      },
      pagination: {
        currentPage: 1,
        totalItemCount: undefined,
        pageCount: undefined,
        count: undefined,
        pageSize: DEFAULT_PAGE_SIZE,
      },
      results: [],
      isVisibleCreateContract: false,
    };
  },
  computed: {
    headerConfig() {
      return {
        title: 'Editor de Contratos',
        icon: 'fas fa-file-signature',
        routes: ['Inicio', 'USPQRP', 'Editor de Contratos'],
      };
    },
    headerActionsConfig() {
      return [
        {
          text: 'Regresar',
          icon: 'fas fa-arrow-circle-left fa-fw',
          variant: 'outline-success',
          click: () => this.$router.go(-1),
        },
        {
          text: 'Crear Contrato',
          icon: 'fas fa-file-plus mr-2',
          variant: 'success',
          visible: true,
          click: () => this.handleCreateContract(),
        },
      ];
    },
    filtersConfig() {
      return [
        {
          name: 'academicYear',
          component: 'CustomDropDown',
          options: this.filterOptions.academicYears,
          placeholder: 'Seleccione',
          fieldName: 'name',
          fieldKey: 'id',
          class: 'col-12 col-md-6',
        },
        {
          name: 'contractType',
          component: 'CustomDropDown',
          options: this.filterOptions.contractTypes,
          placeholder: 'Tipo de Contrato',
          fieldName: 'name',
          fieldKey: 'id',
          class: 'col-12 col-md-6',
        },
      ];
    },
    hasPersistedData() {
      return !!this.moduleData[COMPONENT_NAME];
    },
  },
  async mounted() {
    await this.getInitFilters();
  },
  beforeRouteEnter(to, from, next) {
    next((vm) => (vm.previousRoute = from));
  },
  destroyed() {
    this.persistData();
  },
  methods: {
    async getInitFilters() {
      const { filters, collections } = (await _getInitFilters()).data;
      this.filterOptions = {
        ...collections,
      };
      this.filterModel = {
        ...(this.hasPersistedData ? this.filterModel : filters),
      };
    },
    async handleSearch() {
      const payload = {
        academicYearId: this.filterModel.academicYear?.id,
        contractTypeId: this.filterModel.contractType?.id,
        pageIndex: 1,
        pageSize: this.pagination.pageSize,
      };
      await this.search(payload);
    },
    async search(payload) {
      const response = await _search(payload).catch((err) => {
        this.ShowToast('Alerta', err.response.data.message, 'error');
      });
      if (response?.data) {
        this.results = response.data.data.map((x) => ({
          ...x,
          isOpen: false,
          action: null,
        }));
        this.pagination = {
          ...this.pagination,
          count: response.count,
          currentPage: response.currentPage,
          pageCount: response.pageCount,
          totalItemCount: response.totalItemCount,
        };
      }
    },
    handleToggle({ id }) {
      this.results
        .filter((x) => x.id !== id)
        .forEach((x) => (x.isOpen = false));
    },
    handleCreateContract() {
      this.isVisibleCreateContract = true;
    },
    handleCreateContractCancel() {
      this.isVisibleCreateContract = false;
    },
    async handleCreateContractContinue({ academicYear, contractType }) {
      const academicYearId = academicYear?.id;
      const contractTypeId = contractType?.id;
      if (academicYearId && contractTypeId) {
        const response = await _checkForExistingContractEditor(
          academicYearId,
          contractTypeId
        ).catch((err) => {
          this.ShowToast('Alerta', err.response.data.message, 'error');
          return { error: true };
        });

        const hasError = response?.error;

        if (hasError) return;

        if (!response?.data?.academicYearId) {
          this.$router.push({
            name: CONTRACT_EDITOR_MANAGEMENT_ROUTE_NAME,
            params: {
              academicYearId,
              contractTypeId,
            },
          });
          this.handleCreateContractCancel();
        }

        if (response?.data) {
          const answer = await this.ShowCancelModal(
            'Confirmación',
            '¿Desea incluir los cambios realizados en el contrato del año académico anterior?',
            'Sí',
            'No'
          );
          if (answer) {
            const saveContractEditorResponse = await _saveContractEditor(
              response.data
            ).catch((err) => {
              this.ShowToast(
                'Alerta',
                err.response.data.message || 'error',
                'error'
              );
            });
            if (saveContractEditorResponse?.data) {
              const versionId = saveContractEditorResponse.data?.versionId;
              this.$router.push({
                name: CONTRACT_EDITOR_MANAGEMENT_ROUTE_NAME,
                params: {
                  academicYearId,
                  contractTypeId,
                  versionId: versionId,
                },
              });
              this.handleCreateContractCancel();
            }
          } else {
            this.$router.push({
              name: CONTRACT_EDITOR_MANAGEMENT_ROUTE_NAME,
              params: {
                academicYearId,
                contractTypeId,
              },
            });
            this.handleCreateContractCancel();
          }
        }
      }
    },
    handleViewVersion(contract, version) {
      const { academicYearId, contractTypeId } = contract;
      const { id: versionId } = version;
      this.$router.push({
        name: CONTRACT_EDITOR_MANAGEMENT_ROUTE_NAME,
        params: {
          academicYearId,
          contractTypeId,
          versionId,
        },
      });
    },
    handleEdit(row) {
      const {
        academicYearId,
        contractTypeId,
        currentVersionId: versionId,
      } = row;
      this.$router.push({
        name: CONTRACT_EDITOR_MANAGEMENT_ROUTE_NAME,
        params: {
          academicYearId,
          contractTypeId,
          versionId,
        },
      });
    },
    async handleCreateNewVersion(row) {
      const { academicYearId, contractTypeId, currentVersionId } = row;
      if (academicYearId && contractTypeId && currentVersionId) {
        const payload = {
          academicYearId,
          contractTypeId,
          versionId: currentVersionId,
        };
        const response = await _createNewVersion(payload).catch((err) => {
          this.ShowToast('Alerta', err.response.data.message, 'error');
        });
        if (response?.data) {
          this.ShowToast(
            'Proceso exitoso',
            'La nueva versión ha sido creada exitosamente.',
            'success'
          );
          await this.handleSearch();
        }
      }
    },
    async handleDiscardDraft(row) {
      const { academicYearId, contractTypeId, currentVersionId } = row;
      if (academicYearId && contractTypeId && currentVersionId) {
        const answer = await this.ShowCancelModal(
          'Atención',
          `¿Está seguro que desea descartar este Borrador?`,
          'Sí',
          'No'
        );
        if (!answer) return;
        const payload = {
          academicYearId,
          contractTypeId,
          versionId: currentVersionId,
        };
        const response = await _discardDraftVersion(payload).catch((err) => {
          this.ShowToast('Alerta', err.response.data.message, 'error');
        });
        if (response?.data) {
          this.ShowToast(
            'Proceso exitoso',
            'El borrador ha sido descartado exitosamente.',
            'success'
          );
          await this.handleSearch();
        }
      }
    },
  },
};
</script>

<style scoped lang="scss"></style>
